import { useEffect, useRef, useState, type ReactNode } from "react";
import { CopyChip } from "./CopyChip";
import { audio } from "../lib/audio";
import { useSim } from "../state/store";

/**
 * The persistent mission panel (top-right): ONE translucent panel.
 *   - TOP: step counter + slim progress bar (+ "Voice scenario" pill on call
 *     beats), title, compact guidance, optional action buttons.
 *   - BOTTOM (only when `showTranscript` and the sim store has lines): a thin
 *     divider, a "Call transcript" header with a LIVE dot, and the live
 *     conversation (latest lines in view, full history scrolls inside).
 *   - Narration strip: while a line is spoken (and no transcript section is
 *     showing) the current line is shown as a short caption for hearing-
 *     impaired learners, in addition to the bottom caption bar.
 *
 * Set `showVoice={false}` to suppress the narration strip (walkable guide).
 */
export function GuideTooltip({
  step,
  title,
  body,
  copies,
  cta,
  tip,
  actionLabel,
  actionLabels,
  onAction,
  className,
  showVoice = true,
  showTranscript = false,
  transcriptLive = true,
  whatIf,
  whatIfLabel,
  altAction,
}: {
  step: string;
  title: string;
  body: string;
  copies?: { label: string; value: string }[];
  /** Optional short call-to-action line rendered in its own highlighted
   *  strip beneath the body (e.g. pointing at a clickable 3D prop). */
  cta?: string;
  /** Optional best-practice hint rendered in its own strip with an info icon
   *  (e.g. "Ask staff to confirm the network by name, or use mobile data."). */
  tip?: string;
  /** Phrase inside `body` rendered as an inline action. It should trigger the
   * same handler as the highlighted control inside the active device. */
  actionLabel?: string;
  /** Every phrase in `body` that should be its own bold action. */
  actionLabels?: string[];
  onAction?: (label?: string) => void;
  /** extra class(es) appended to .guide (e.g. walkable-mode elevation). */
  className?: string;
  /** Render the narration caption strip while a line is spoken (default true). */
  showVoice?: boolean;
  /** Voice/video call panel: show the "Voice scenario" pill, action buttons
   *  and (when the sim store holds lines) the live call transcript. */
  showTranscript?: boolean;
  /** Whether the call is still connected (drives the LIVE dot). */
  transcriptLive?: boolean;
  /** Hypothetical being replayed after a safe choice (store `whatIf`). */
  whatIf?: string | null;
  whatIfLabel?: string;
  /** Second option for a beat whose device only offers the risky action
   *  (beat `safeChoice`). */
  altAction?: { label: string; orLabel?: string; onClick: () => void };
}) {
  const [caption, setCaption] = useState<string | null>(audio.getCaption());
  const [speaking, setSpeaking] = useState<boolean>(audio.isSpeaking());
  const transcript = useSim((s) => s.transcript);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const hasTranscript = showTranscript && transcript.length > 0;

  const labels = (actionLabels && actionLabels.length ? actionLabels : actionLabel ? [actionLabel] : []).filter(
    (label) =>
      label &&
      (body.toLowerCase().includes(label.toLowerCase()) ||
        (tip ?? "").toLowerCase().includes(label.toLowerCase())),
  );
  // Call panels show the labels as real buttons under the guidance; every
  // other guide keeps the bold inline actions inside the text.
  const buttonMode = showTranscript && !!onAction && labels.length > 0;

  const linkText = (text: string): ReactNode => {
    if (buttonMode || !labels.length || !onAction) return text;
    const lower = text.toLowerCase();
    const hits: { start: number; end: number; label: string }[] = [];
    for (const label of labels) {
      const start = lower.indexOf(label.toLowerCase());
      if (start < 0) continue;
      const end = start + label.length;
      if (hits.some((h) => start < h.end && end > h.start)) continue;
      hits.push({ start, end, label });
    }
    hits.sort((a, b) => a.start - b.start);
    if (!hits.length) return text;
    const parts: ReactNode[] = [];
    let cursor = 0;
    hits.forEach((hit, index) => {
      if (hit.start > cursor) parts.push(text.slice(cursor, hit.start));
      parts.push(
        <button key={index} type="button" className="guide-inline-action" onClick={() => onAction?.(hit.label)}>
          {text.slice(hit.start, hit.end)}
        </button>,
      );
      cursor = hit.end;
    });
    if (cursor < text.length) parts.push(text.slice(cursor));
    return parts;
  };

  useEffect(() => {
    if (!showVoice) return;
    const offC = audio.subscribeCaption(setCaption);
    const offS = audio.subscribeSpeaking(setSpeaking);
    return () => {
      offC();
      offS();
    };
  }, [showVoice]);

  // Keep the newest transcript line in view without growing the panel.
  useEffect(() => {
    if (!hasTranscript) return;
    const el = transcriptRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [transcript, hasTranscript]);

  // "16 / 19" -> slim progress bar.
  const m = /^\s*(\d+)\s*\/\s*(\d+)\s*$/.exec(step);
  const pct = m && Number(m[2]) > 0 ? Math.min(100, Math.max(0, (Number(m[1]) / Number(m[2])) * 100)) : null;

  const t0 = transcript.length ? transcript[0].at : 0;
  const stamp = (at: number): string => {
    const s = Math.max(0, Math.round((at - t0) / 1000));
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  const showNarration = showVoice && !hasTranscript && speaking && !!caption;

  return (
    <div className={`guide${hasTranscript ? " guide--call" : ""}${className ? ` ${className}` : ""}`}>
      <div className="guide-story">
        {whatIf && (
          <div className="cf-whatif">
            <span className="cf-whatif__label">{whatIfLabel ?? "What would have happened"}</span>
            <span className="cf-whatif__text">{whatIf}</span>
          </div>
        )}
        <div className="guide-head">
          <span className="step">{step}</span>
          {showTranscript && <span className="guide-pill">Voice scenario</span>}
        </div>
        {pct !== null && (
          <div className="guide-progress" aria-hidden>
            <span style={{ width: `${pct}%` }} />
          </div>
        )}
        <h4>{title}</h4>
        <p>{linkText(body)}</p>
        {buttonMode && (
          <div className="guide-actions">
            {labels.map((label, i) => (
              <button
                key={label}
                type="button"
                className={`guide-action-btn${i === 0 ? " is-primary" : ""}`}
                onClick={() => onAction?.(label)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {copies && copies.length > 0 && (
          <div className="guide-copies">
            {copies.map((c, i) => (
              <CopyChip key={i} label={c.label} value={c.value} />
            ))}
          </div>
        )}
        {cta && <p className="guide-cta">{cta}</p>}
        {altAction && (
          <div className="guide-alt">
            <span className="guide-alt__or">{altAction.orLabel ?? "Or"}</span>
            <button type="button" className="guide-alt__btn" onClick={altAction.onClick}>
              {altAction.label}
            </button>
          </div>
        )}
        {tip && (
          <p className="guide-tip">
            <svg className="guide-tip-ico" viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="12" cy="8" r="1.15" fill="currentColor" />
              <path d="M12 11.2v5.4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
            </svg>
            <span>{linkText(tip)}</span>
          </p>
        )}
      </div>
      {hasTranscript && (
        <div className="guide-transcript" role="log" aria-live="polite">
          <div className="guide-transcript-head">
            <span className="guide-transcript-bars" aria-hidden>
              <i />
              <i />
              <i />
            </span>
            <span className="guide-transcript-title">Call transcript</span>
            {transcriptLive && (
              <span className="guide-live">
                <i aria-hidden />
                Live
              </span>
            )}
          </div>
          <div className="guide-transcript-lines" ref={transcriptRef}>
            {transcript.map((l) => (
              <div key={l.id} className={`guide-tline ${l.role}${l.final ? "" : " tentative"}`}>
                <div className="guide-tline-meta">
                  <span className="guide-tline-who">{l.role === "attacker" ? "Caller" : "You"}</span>
                  <span className="guide-tline-time">{stamp(l.at)}</span>
                </div>
                <div className="guide-tline-text">{l.text}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {showNarration && (
        <div className="guide-voice guide-voice--speaking" role="status" aria-live="polite">
          <div className="guide-voice-head">
            <span className="guide-voice-bars" aria-hidden>
              <span />
              <span />
              <span />
              <span />
            </span>
            <span className="guide-voice-label">Voice speaking</span>
          </div>
          <div className="guide-voice-text">{caption}</div>
        </div>
      )}
    </div>
  );
}
